/**
 * @file StatementStepReview.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 *
 * Generate Statements, step 3 — Review: the statements beside the selected
 * one's PDF, drawn with the run's Contents. Approving moves to the next row,
 * so nobody scrolls between a table and a preview window (Cam, 20 Oct 2026:
 * "the vertical scrolling is problematic … especially at the approval
 * step"). The table scrolls inside its own card under a fixed header.
 *
 *   A approve and next · U unapprove and next · N note · ↑ ↓ (or K J) move
 *
 * Statements the Xero check found wrong stay first, marked red (the screen
 * sorts them so).
 * 1.0.1: showXeroCheck — off for an interim run (no Xero check column or
 *        filter while approving).
 * 2.0.0 (2026-10-20, Cam — the statements look):
 *   - a selection tick leads each row; ticking rows swaps the filters for a
 *     bulk bar (Approve, Unapprove, Bundle invoices, Statement only), which
 *     replaces the six-item Approve menu. "Approve the rest" stays one click.
 *     Ticking every statement and approving uses the run's approve-all.
 *   - whether a customer's invoices are bundled is a toggle in the Invoices
 *     cell;
 *   - at narrow widths (≤ 1200px) the preview opens as a drawer from the
 *     right, over the list (a row click opens it; Escape or × closes it). The
 *     keys work while it is open; never behind a window or a question.
 * 2.1.0 (2026-10-20, Cam — statement notes): an "Office note" column (the
 *     office's own note, only seen here) and an "On statement" badge by the
 *     name when there is a note for the customer; the Notes filter counts
 *     either; the preview's notice shows both, each said for what it is; the
 *     preview is drawn again when the customer note changes.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import StatementTable from './StatementTable';
import StatementPdfPane from './StatementPdfPane';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import { accountLabel } from '../utils/statementOrder';
import { NOTE_FIELDS, hasAnyNote, hasOfficeNote, hasCustomerNote, previewNoteKey } from '../utils/statementNotes';

export const REVIEW_FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'todo', label: 'To review', test: (s) => (s.approval_status || 'unchecked') === 'unchecked' },
  { key: 'approved', label: 'Approved', test: (s) => s.approval_status === 'approved' },
  { key: 'unapproved', label: 'Unapproved', test: (s) => s.approval_status === 'unapproved' },
  { key: 'mismatch', label: 'Doesn’t match Xero', short: 'Mismatch', test: (s) => s.integrity?.status === 'mismatch', tone: 'error' },
  // Either note: the office's own, or the one for the customer.
  { key: 'note', label: 'Has a note', short: 'Notes', hint: 'Has an office note or a note for the customer', test: hasAnyNote }
];

/** Below this width the preview is a drawer over the list. */
export const NARROW_QUERY = '(max-width: 1200px)';

// Any window or question open (Modal, AlertDialog, ConfirmDialog) — but not
// the preview drawer itself, which is where the keys are meant to work.
const BLOCKING = '.modal-overlay, .confirm-dialog-overlay, [role="dialog"]:not(.sr-drawer)';

function typing(target) {
  return !!target && typeof target.closest === 'function' && !!target.closest('input, textarea, select, [contenteditable="true"]');
}

function useNarrow() {
  const query = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(NARROW_QUERY) : null);
  const [narrow, setNarrow] = useState(() => !!query()?.matches);
  useEffect(() => {
    const mq = query();
    if (!mq) return undefined;
    const on = (e) => setNarrow(!!e.matches);
    setNarrow(!!mq.matches);
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', on);
    else if (typeof mq.addListener === 'function') mq.addListener(on);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', on);
      else if (typeof mq.removeListener === 'function') mq.removeListener(on);
    };
  }, []);
  return narrow;
}

function ApprovalBadge({ status }) {
  if (status === 'approved') return <span className="approval-badge approved"><span className="material-symbols-rounded" aria-hidden="true">check</span>Approved</span>;
  if (status === 'unapproved') return <span className="approval-badge unapproved"><span className="material-symbols-rounded" aria-hidden="true">block</span>Unapproved</span>;
  return <span className="approval-badge unchecked">To review</span>;
}

function Slide({ icon, label, onClick, disabled, title, className = '', out = true, kbd, write }) {
  return (
    <div>
      <button type="button" className={`btn-slideout${out ? ' always-out' : ''}${className ? ` ${className}` : ''}`}
        onClick={onClick} disabled={disabled} title={title || label} data-write-action={write ? true : undefined}>
        <div className="icon-wrapper"><span className="material-symbols-rounded">{icon}</span></div>
        <span className="btn-label">{label}{kbd && <kbd className="sr-kbd">{kbd}</kbd>}</span>
      </button>
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array} props.statements - in the order shown (mismatches first)
 * @param {function} props.onApprove - (id, 'approved'|'unapproved') => Promise
 * @param {function} props.onNote - (id)
 * @param {function} props.onBulk - ('approve-all'|'approve-unchecked'|'unapprove-all'|{ ids, action, scope })
 * @param {object} props.table - StatementTable props for the Xero check and the invoices toggle
 *        (+ onSetInclude(ids, include) for the bulk bar)
 * @param {object} props.content
 * @param {'email'|'print'} props.template
 * @param {function} props.onTemplate
 * @param {boolean} props.active - this step is on screen (keys only then)
 * @param {string} props.filter
 * @param {function} props.onFilter
 * @param {boolean} [props.showXeroCheck]
 */
function StatementStepReview({ statements, onApprove, onNote, onBulk, table, content, template, onTemplate, active, filter, onFilter, showXeroCheck = true }) {
  const [selectedId, setSelectedId] = useState(null);
  const [ticked, setTicked] = useState(() => new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const narrow = useNarrow();
  const tableRef = useRef(null);
  const drawerRef = useRef(null);

  // An interim run's Xero check is not part of approving it (Cam, 20 Oct 2026):
  // no Xero check column or filter here.
  const filters = showXeroCheck ? REVIEW_FILTERS : REVIEW_FILTERS.filter(f => f.key !== 'mismatch');
  const shown = useMemo(() => {
    const f = filters.find(x => x.key === filter) || filters[0];
    return statements.filter(f.test);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statements, filter, showXeroCheck]);
  const counts = useMemo(() => Object.fromEntries(REVIEW_FILTERS.map(f => [f.key, statements.filter(f.test).length])), [statements]);

  // Ticks only ever count rows on screen (a filter change leaves the others out).
  const shownIds = useMemo(() => new Set(shown.map(s => s.id)), [shown]);
  const tickedShown = useMemo(() => new Set([...ticked].filter(id => shownIds.has(id))), [ticked, shownIds]);
  const tickedList = shown.filter(s => tickedShown.has(s.id));
  const allShownTicked = shown.length > 0 && tickedShown.size === shown.length;

  // Keep a row selected: the first on screen, or the next one along.
  useEffect(() => {
    if (!shown.length) { if (selectedId !== null) setSelectedId(null); return; }
    if (!shown.some(s => s.id === selectedId)) setSelectedId(shown[0].id);
  }, [shown, selectedId]);

  // The selected row in view.
  useEffect(() => {
    if (!selectedId || !tableRef.current) return;
    const row = tableRef.current.querySelector(`tr[data-statement-id="${selectedId}"]`);
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  // The drawer closes when the window is wide again; it takes focus when it opens.
  useEffect(() => { if (!narrow && drawerOpen) setDrawerOpen(false); }, [narrow, drawerOpen]);
  useEffect(() => {
    if (narrow && drawerOpen && drawerRef.current && typeof drawerRef.current.focus === 'function') drawerRef.current.focus();
  }, [narrow, drawerOpen]);

  useSlideOutButton([selectedId, tickedShown.size, narrow, drawerOpen, filter]);

  const index = shown.findIndex(s => s.id === selectedId);
  const selected = index >= 0 ? shown[index] : null;
  const move = (delta) => {
    if (!shown.length) return;
    const next = Math.min(shown.length - 1, Math.max(0, (index < 0 ? 0 : index) + delta));
    setSelectedId(shown[next].id);
  };
  const decideAndNext = async (status) => {
    if (!selected) return;
    const nextId = shown[index + 1]?.id ?? null;
    await onApprove(selected.id, status);
    if (nextId) setSelectedId(nextId);
  };

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || typing(e.target)) return;
      // Not while any window or question is open (Modal, AlertDialog, ConfirmDialog).
      if (document.querySelector(BLOCKING)) return;
      const k = e.key;
      if (k === 'Escape' && narrow && drawerOpen) { e.preventDefault(); setDrawerOpen(false); }
      else if (k === 'ArrowDown' || k === 'j') { e.preventDefault(); move(1); }
      else if (k === 'ArrowUp' || k === 'k') { e.preventDefault(); move(-1); }
      else if (k === 'a' || k === 'A') { e.preventDefault(); decideAndNext('approved'); }
      else if (k === 'u' || k === 'U') { e.preventDefault(); decideAndNext('unapproved'); }
      else if ((k === 'n' || k === 'N') && selected) { e.preventDefault(); onNote(selected.id); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const filterName = (filters.find(f => f.key === filter) || filters[0]).label.toLowerCase();

  // ── Ticks and the bulk bar ───────────────────────────────────────────────
  const toggleTick = (id) => setTicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const tickAll = (on) => setTicked(on ? new Set(shown.map(s => s.id)) : new Set());
  const clearTicks = () => setTicked(new Set());
  const everyStatement = tickedShown.size > 0 && tickedShown.size === statements.length;
  const bulkDecide = (action) => {
    const ids = tickedList.map(s => s.id);
    clearTicks();
    // Every statement ticked is the run's own approve-all / unapprove-all.
    if (everyStatement) onBulk(action === 'approve' ? 'approve-all' : 'unapprove-all');
    else onBulk({ ids, action, scope: 'selected' });
  };
  const withInvoices = tickedList.filter(s => (s.invoiceCount || 0) > 0);
  const bundle = (include) => {
    const ids = withInvoices.map(s => s.id);
    if (!ids.length || !table.includeInvoiceIds) return;
    if (typeof table.onSetInclude === 'function') { table.onSetInclude(ids, include); return; }
    ids.filter(id => table.includeInvoiceIds.has(id) !== include).forEach(id => table.onToggleInclude?.(id));
  };
  const allBundled = withInvoices.length > 0 && withInvoices.every(s => table.includeInvoiceIds?.has(s.id));
  const noneBundled = withInvoices.every(s => !table.includeInvoiceIds?.has(s.id));
  const toReview = counts.todo || 0;

  const preview = (
    <StatementPdfPane
      statementId={selected?.id || null}
      noteKey={previewNoteKey(selected?.note)}
      content={content}
      template={template}
      onTemplate={onTemplate}
      heading={selected ? (
        <>
          <span className="sr-preview-name">{accountLabel(selected.accountNo, selected.account)}</span>
          <ApprovalBadge status={selected.approval_status || 'unchecked'} />
        </>
      ) : null}
      headExtra={narrow ? (
        <button type="button" className="btn-icon sr-drawer-close" onClick={() => setDrawerOpen(false)} title="Close the preview (Esc)" aria-label="Close the preview">
          <span className="material-symbols-rounded">close</span>
        </button>
      ) : null}
      notice={selected ? (
        <>
          {showXeroCheck && selected.integrity?.status === 'mismatch' && (
            <p className="sr-preview-alert" role="alert">
              <span className="material-symbols-rounded">error</span>
              <span>
                <strong>Doesn&rsquo;t match Xero.</strong>{selected.integrity?.headline ? ` ${selected.integrity.headline}.` : ''}
                {table.onIntegrityClick && (
                  <button type="button" className="sr-link" onClick={() => table.onIntegrityClick(selected)}>Open the Xero check</button>
                )}
              </span>
            </p>
          )}
          {hasCustomerNote(selected) ? (
            <p className="sr-preview-note customer" title={`Note for the customer — printed on the statement and in the email: ${String(selected.note).trim()}`}>
              <span className="material-symbols-rounded">{NOTE_FIELDS.note.icon}</span>
              <span><strong>On the statement and in the email:</strong> {String(selected.note).trim()}</span>
            </p>
          ) : null}
          {hasOfficeNote(selected) ? (
            <p className="sr-preview-note office" title={`Office note (only seen here): ${String(selected.office_note).trim()}`}>
              <span className="material-symbols-rounded">{NOTE_FIELDS.office_note.icon}</span>
              <span><strong>Office note (only seen here):</strong> {String(selected.office_note).trim()}</span>
            </p>
          ) : null}
        </>
      ) : null}
      actions={selected ? (
        <div className="sr-decide">
          <div className="buttons slideout">
            <Slide icon="check" label="Approve & next" kbd="A" className="sr-approve" write
              onClick={() => decideAndNext('approved')} disabled={selected.approval_status === 'approved'}
              title={selected.approval_status === 'approved' ? 'Already approved' : 'Approve, and move to the next (A)'} />
            <Slide icon="block" label="Unapprove & next" kbd="U" write
              onClick={() => decideAndNext('unapproved')} disabled={selected.approval_status === 'unapproved'}
              title={selected.approval_status === 'unapproved' ? 'Already unapproved' : 'Unapprove, and move to the next (U)'} />
            <Slide icon="sticky_note_2" label="Notes" kbd="N" out={false} write onClick={() => onNote(selected.id)} title="The office note and the note for the customer (N)" />
          </div>
          <span className="sr-keys">
            <kbd className="sr-kbd">A</kbd> approve · <kbd className="sr-kbd">U</kbd> unapprove · <kbd className="sr-kbd">N</kbd> note · <kbd className="sr-kbd">↑</kbd><kbd className="sr-kbd">↓</kbd> move{narrow ? <> · <kbd className="sr-kbd">Esc</kbd> close</> : null} · {index + 1} of {shown.length}
          </span>
        </div>
      ) : null}
    />
  );

  const drawer = narrow && drawerOpen;

  return (
    <div className={`sr-review${narrow ? ' narrow' : ''}`}>
      <section className="sr-panel sr-review-list">
        {tickedShown.size > 0 ? (
          <div className="sr-bulkbar" role="toolbar" aria-label="Ticked statements">
            <TickCheckbox id="stmt-bulk-all" checked={allShownTicked} onChange={() => tickAll(!allShownTicked)}
              ariaLabel={allShownTicked ? 'Untick every statement shown' : 'Tick every statement shown'} />
            <strong>{tickedShown.size} selected</strong>
            {!allShownTicked && <button type="button" className="sr-link" onClick={() => tickAll(true)}>Select all {shown.length}</button>}
            <span className="sr-spacer" />
            <div className="buttons slideout">
              <Slide icon="check" label="Approve" className="sr-approve" write onClick={() => bulkDecide('approve')}
                title={showXeroCheck ? 'Approve the ticked statements — any that don’t match Xero are left out' : 'Approve the ticked statements'} />
              <Slide icon="block" label="Unapprove" write onClick={() => bulkDecide('unapprove')} title="Unapprove the ticked statements" />
              <Slide icon="receipt_long" label="Bundle invoices" write onClick={() => bundle(true)}
                disabled={!withInvoices.length || allBundled}
                title={!withInvoices.length ? 'None of the ticked statements has invoices' : allBundled ? 'Their invoices are already bundled' : 'Send the ticked customers’ invoices with their statements'} />
              <Slide icon="do_not_disturb_on" label="Statement only" write onClick={() => bundle(false)}
                disabled={!withInvoices.length || noneBundled}
                title={!withInvoices.length ? 'None of the ticked statements has invoices' : noneBundled ? 'Already statement only' : 'Send the ticked customers their statement without the invoices'} />
              <Slide icon="close" label="Clear" out={false} onClick={clearTicks} title="Untick them all" />
            </div>
          </div>
        ) : (
          <div className="sr-toolbar">
            <div className="sr-seg" role="tablist" aria-label="Show">
              {filters.map(f => (
                <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
                  aria-label={f.short ? `${f.label} (${counts[f.key]})` : undefined} title={f.hint || (f.short ? f.label : undefined)}
                  className={filter === f.key ? 'on' : ''}
                  onClick={() => onFilter(f.key)} disabled={f.key !== 'all' && !counts[f.key] && filter !== f.key}>
                  {f.short || f.label} <span className={`sr-count${f.tone && counts[f.key] ? ` ${f.tone}` : ''}`}>{counts[f.key]}</span>
                </button>
              ))}
            </div>
            <span className="sr-spacer" />
            <button type="button" className="sr-menu-btn" onClick={() => onBulk('approve-unchecked')} disabled={!toReview} data-write-action
              title={!toReview ? 'Every statement has been reviewed' : `Approve the ${toReview} not reviewed yet${showXeroCheck ? ' — any that don’t match Xero are left out' : ''}. Tick rows to approve or unapprove some.`}>
              <span className="material-symbols-rounded">done_all</span> Approve the rest
            </button>
          </div>
        )}
        <div className="sr-review-table" ref={tableRef}>
          <StatementTable
            {...table}
            statements={shown}
            columns={showXeroCheck
              ? ['accountNo', 'company', 'officeNote', 'invoiceCount', 'balance', 'approval', 'integrity']
              : ['accountNo', 'company', 'officeNote', 'invoiceCount', 'balance', 'approval']}
            onRowClick={(s) => { setSelectedId(s.id); if (narrow) setDrawerOpen(true); }}
            selectedId={selectedId}
            selectedIds={tickedShown}
            onToggleSelect={toggleTick}
            onToggleSelectAll={tickAll}
            emptyMessage={statements.length ? `No statements are ${filterName}.` : 'No statements'}
            actions={[
              {
                type: 'note',
                label: (s) => (hasAnyNote(s)
                  ? `Notes — ${[hasOfficeNote(s) && 'office note', hasCustomerNote(s) && 'note for the customer'].filter(Boolean).join(' and ')}`
                  : 'Add a note (for the office, or for the customer)'),
                icon: (s) => (hasAnyNote(s) ? 'sticky_note_2' : 'note_add'),
                handler: (s) => onNote(s.id)
              },
              {
                type: 'approve', label: 'Approve', icon: 'check',
                handler: (s) => onApprove(s.id, 'approved'),
                isDisabled: (s) => s.approval_status === 'approved',
                disabledTitle: () => 'Already approved'
              },
              {
                type: 'unapprove', label: 'Unapprove', icon: 'block',
                handler: (s) => onApprove(s.id, 'unapproved'),
                isDisabled: (s) => s.approval_status === 'unapproved',
                disabledTitle: () => 'Already unapproved'
              }
            ]}
          />
        </div>
      </section>

      {narrow && <div className={`sr-drawer-scrim${drawer ? ' open' : ''}`} onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      <section
        ref={drawerRef}
        className={`sr-panel sr-review-preview${drawer ? ' open sr-drawer' : ''}`}
        role={drawer ? 'dialog' : undefined}
        aria-modal={drawer ? 'false' : undefined}
        aria-label={drawer ? 'Statement preview' : undefined}
        tabIndex={drawer ? -1 : undefined}
      >
        {preview}
      </section>
    </div>
  );
}

export default StatementStepReview;
